/**
 * StudioInspector — properties for the selected component.
 *
 * Read-only facts (kind, dims, area, both coordinate frames, dimension-basis
 * provenance, OEM inclusion flags) plus the geometry edits the studio owns:
 * pin/unpin packed equipment, numeric position/rotation for pinned items and
 * rect customs, and custom deletion. Every edit routes through the pure
 * patch builders, which stamp `schemaVersion: 2` on the frozen layout.
 */
import React, { useEffect, useState } from 'react';
import { Pin, PinOff, Trash2 } from 'lucide-react';

import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import type {
  GenerationAcousticBarrier,
  GenerationLayoutConfiguration,
  GenerationLayoutExclusionZone,
  GenerationProjectInstalledReservations,
} from '../../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../../types/siteBoundary';
import type { SiteLayer, SiteLayerCategory } from '../../../../types/site';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../../../config/siteLayerColors';
import { LAYER_COLOR_PRESETS } from '../../../../lib/siteLayerEditor';
import {
  removeCustomComponent,
  removeManualPlacement,
  renameComponent,
  resolveCustomComponent,
  patchCustomComponent,
  upsertManualPlacement,
} from './studioLayoutEdits';
import { describeBarrier, describeExclusion, describeSiteLayer } from './studioLayers';

const METRES_PER_FOOT = 0.3048;

const BASIS_LABELS: Record<string, string> = {
  'planning-acreage': 'Planning acreage surrogate',
  'project-installed-dimensions': 'OEM installed dimensions',
  'project-route-reservation': 'Project route reservation',
  'project-construction-reservation': 'Construction reservation',
  'user-defined': 'User-defined geometry',
};

export interface StudioInspectorProps {
  testFit: GenerationParcelTestFitResult;
  activeLayout: GenerationLayoutConfiguration;
  installedReservations?: GenerationProjectInstalledReservations;
  readOnly: boolean;
  selectedId: string | null;
  /** Site-reference layers, so selecting one populates the Inspector too. */
  siteLayers?: readonly SiteLayer[];
  /**
   * Read-only gate for SITE-LAYER edits specifically. Site layers are site
   * truth (editable from the Site Map regardless of scenario state), so this is
   * driven by host permission + the layer lock — NOT the frozen-layout
   * `readOnly` used for components. Defaults to `readOnly` for back-compat.
   */
  siteLayerReadOnly?: boolean;
  /** Whether the selected site layer is locked (drives the read-only reason). */
  selectedLayerLocked?: boolean;
  /** Boundary parcels, acoustic barriers — selecting any populates the dock. */
  parcels?: readonly SiteBoundaryParcel[];
  acousticBarriers?: readonly GenerationAcousticBarrier[];
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void;
  onSelect: (id: string | null) => void;
  /** Edit a site-reference layer's own fields (name/colour/category). SITE TRUTH
   *  — persisted via `updateSiteLayers`, never the frozen layout. Omit to keep
   *  the site-layer card read-only. */
  onPatchSiteLayer?: (id: string, patch: Partial<Pick<SiteLayer, 'name' | 'color' | 'category' | 'customCategory'>>) => void;
  /** Remove a site-reference layer from the site record. */
  onDeleteSiteLayer?: (id: string) => void;
}

/**
 * Property card for a selected site-reference layer. When edit callbacks are
 * supplied the name/colour/category are editable and the layer is deletable —
 * all SITE TRUTH, persisted through `updateSiteLayers`, never the frozen layout.
 * Without callbacks (or when read-only) it degrades to a facts-only view.
 */
const SiteLayerCard: React.FC<{
  layer: SiteLayer;
  readOnly: boolean;
  locked?: boolean;
  onPatchSiteLayer?: (id: string, patch: Partial<Pick<SiteLayer, 'name' | 'color' | 'category' | 'customCategory'>>) => void;
  onDeleteSiteLayer?: (id: string) => void;
}> = ({ layer, readOnly, locked = false, onPatchSiteLayer, onDeleteSiteLayer }) => {
  const facts = describeSiteLayer(layer);
  const editable = !readOnly && onPatchSiteLayer != null;
  // Name commits on blur/Enter; the draft is keyed to the layer id so switching
  // selection never leaks one layer's unsaved name onto another.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  // Custom-category draft: commit on blur/Enter. Empty string clears the
  // override (back to the enum category). Keyed to the layer id like the name.
  const [customDraft, setCustomDraft] = useState<string | null>(null);
  useEffect(() => {
    setNameDraft(null);
    setCustomDraft(null);
  }, [layer.id]);
  const commitName = (): void => {
    const next = nameDraft?.trim();
    if (next && next !== layer.name) onPatchSiteLayer?.(layer.id, { name: next });
    setNameDraft(null);
  };
  const commitCustomCategory = (): void => {
    if (customDraft === null) return;
    const next = customDraft.trim();
    if (next !== (layer.customCategory ?? '')) onPatchSiteLayer?.(layer.id, { customCategory: next });
    setCustomDraft(null);
  };

  return (
    <div className="min-w-0 space-y-3 p-3" data-testid="studio-inspector-site-layer">
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">{facts.categoryLabel}</p>
        {editable
          ? (
              <input
                value={nameDraft ?? layer.name}
                onChange={(event) => setNameDraft(event.target.value)}
                onBlur={commitName}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') setNameDraft(null);
                }}
                aria-label="Layer name"
                data-testid="studio-site-layer-name"
                className="mt-0.5 w-full border border-border bg-background p-1.5 text-xs font-bold text-text-primary rounded-md"
              />
            )
          : (
              <h3 className="mt-0.5 flex items-center gap-1.5 truncate text-xs font-bold text-text-primary" title={facts.name}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: facts.color }} aria-hidden />
                <span className="truncate">{facts.name}</span>
              </h3>
            )}
        <span className="mt-1 inline-block border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-secondary rounded-sm">
          {facts.sourceLabel}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border text-[10px]">
        <div className="bg-background p-2"><dt className="text-text-muted">Geometry</dt><dd className="mt-0.5 font-bold capitalize">{facts.geometryType}</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Vertices</dt><dd className="mt-0.5 font-bold tabular-nums">{facts.vertexCount}</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Visibility</dt><dd className="mt-0.5 font-bold">{facts.visible ? 'Visible' : 'Hidden'}</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Source</dt><dd className="mt-0.5 truncate font-bold" title={facts.sourceLabel}>{facts.sourceLabel}</dd></div>
      </dl>

      {editable && (
        <>
          <div>
            <label htmlFor="studio-site-layer-category" className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Category</label>
            <select
              id="studio-site-layer-category"
              value={layer.category}
              onChange={(event) => onPatchSiteLayer?.(layer.id, { category: event.target.value as SiteLayerCategory })}
              data-testid="studio-site-layer-category"
              className="mt-1 w-full border border-border bg-background p-1.5 text-xs text-text-primary rounded-md"
            >
              {CATEGORY_ORDER.map(category => (
                <option key={category} value={category}>{CATEGORY_LABELS[category]}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="studio-site-layer-custom-category" className="text-[9px] font-bold uppercase tracking-wider text-text-muted">
              Custom category <span className="font-normal normal-case text-text-muted/60">(optional — files under your own heading)</span>
            </label>
            <input
              id="studio-site-layer-custom-category"
              value={customDraft ?? layer.customCategory ?? ''}
              placeholder="e.g. Client markups"
              onChange={(event) => setCustomDraft(event.target.value)}
              onBlur={commitCustomCategory}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') setCustomDraft(null);
              }}
              data-testid="studio-site-layer-custom-category"
              className="mt-1 w-full border border-border bg-background p-1.5 text-xs text-text-primary rounded-md"
            />
          </div>

          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Colour</p>
            <div className="mt-1 flex flex-wrap gap-1" data-testid="studio-site-layer-colors">
              {LAYER_COLOR_PRESETS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onPatchSiteLayer?.(layer.id, { color: preset })}
                  aria-label={`Set colour ${preset}`}
                  aria-pressed={facts.color.toLowerCase() === preset.toLowerCase()}
                  title={preset}
                  className={`h-5 w-5 rounded-sm border ${facts.color.toLowerCase() === preset.toLowerCase() ? 'border-text-primary ring-1 ring-primary' : 'border-border'}`}
                  style={{ backgroundColor: preset }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {facts.description && (
        <p className="border-l-2 border-border pl-2 text-[10px] leading-relaxed text-text-secondary">
          {facts.description}
        </p>
      )}

      {editable && onDeleteSiteLayer && (
        <button
          type="button"
          onClick={() => onDeleteSiteLayer(layer.id)}
          className="flex items-center gap-1 border border-danger px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-danger rounded-md"
          data-testid="studio-site-layer-delete"
          title="Remove this reference layer from the site record"
        >
          <Trash2 className="h-3 w-3" aria-hidden /> Delete layer
        </button>
      )}

      {!editable && (
        <p className="text-[9px] leading-relaxed text-text-muted">
          Reference geometry from the site record.
          {locked ? ' Unlock the layer to edit it.' : readOnly ? ' This view is read-only.' : ''}
        </p>
      )}
    </div>
  );
};

/** A compact read-only facts card used by the non-editable layer types. */
const LayerFactsCard: React.FC<{
  testId: string;
  eyebrow: string;
  title: string;
  color?: string;
  facts: ReadonlyArray<{ label: string; value: string; danger?: boolean }>;
  note?: string;
}> = ({ testId, eyebrow, title, color, facts, note }) => (
  <div className="min-w-0 space-y-3 p-3" data-testid={testId}>
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">{eyebrow}</p>
      <h3 className="mt-0.5 flex items-center gap-1.5 truncate text-xs font-bold text-text-primary" title={title}>
        {color && <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} aria-hidden />}
        <span className="truncate">{title}</span>
      </h3>
    </div>
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border text-[10px]">
      {facts.map(fact => (
        <div key={fact.label} className="bg-background p-2">
          <dt className="text-text-muted">{fact.label}</dt>
          <dd className={`mt-0.5 truncate font-bold ${fact.danger ? 'text-danger' : ''}`} title={fact.value}>{fact.value}</dd>
        </div>
      ))}
    </dl>
    {note && <p className="text-[9px] leading-relaxed text-text-muted">{note}</p>}
  </div>
);

const BarrierCard: React.FC<{ barrier: GenerationAcousticBarrier }> = ({ barrier }) => {
  const facts = describeBarrier(barrier);
  return (
    <LayerFactsCard
      testId="studio-inspector-barrier"
      eyebrow="Acoustic barrier"
      title={facts.label}
      color="#22d3ee"
      facts={[
        { label: 'Type', value: facts.kindLabel },
        { label: 'Height', value: `${facts.heightM.toFixed(1)} m` },
      ]}
      note="Attenuates the noise contour layer — editing height or position updates the noise field."
    />
  );
};

const ExclusionCard: React.FC<{ zone: GenerationLayoutExclusionZone }> = ({ zone }) => {
  const facts = describeExclusion(zone);
  return (
    <LayerFactsCard
      testId="studio-inspector-exclusion"
      eyebrow="No-build exclusion"
      title={facts.label}
      color="#ef4444"
      facts={[
        { label: 'Kind', value: facts.kindLabel },
        { label: 'Source', value: facts.sourceLabel },
        { label: 'Rings', value: String(facts.ringCount) },
      ]}
      note="Equipment may not be placed inside this zone; it constrains the packer."
    />
  );
};

const ParcelCard: React.FC<{ parcel: SiteBoundaryParcel; isTarget: boolean }> = ({ parcel, isTarget }) => (
  <LayerFactsCard
    testId="studio-inspector-parcel"
    eyebrow="Boundary parcel"
    title={parcel.label?.trim() || 'Parcel'}
    facts={[
      { label: 'Area', value: `${parcel.area.toFixed(1)} ac` },
      { label: 'Fit target', value: isTarget ? 'Yes' : 'No' },
    ]}
    note={isTarget ? 'The active test-fit target parcel.' : 'Set this as the fit target from the Parcel control.'}
  />
);

export const StudioInspector: React.FC<StudioInspectorProps> = ({
  testFit,
  activeLayout,
  installedReservations,
  readOnly,
  selectedId,
  siteLayers = [],
  siteLayerReadOnly,
  selectedLayerLocked = false,
  parcels = [],
  acousticBarriers = [],
  onPatch,
  onSelect,
  onPatchSiteLayer,
  onDeleteSiteLayer,
}) => {
  const component = testFit.components
    .find(candidate => candidate.rectangle.id === selectedId) ?? null;
  const rectangle = component?.rectangle ?? null;
  const custom = rectangle ? resolveCustomComponent(activeLayout, rectangle.id) : null;
  const rectShape = custom?.shape.type === 'rect' ? custom.shape : null;
  const pinned = rectangle?.placement === 'pinned' && !custom;
  // A non-component selection may be any other layer type — the Inspector is
  // type-aware so selecting ANY layer populates it (and, when edit callbacks are
  // wired, edits site layers as site truth) rather than showing "select a
  // component". Id spaces are distinct, so first-match resolution is safe.
  const otherId = !component && selectedId != null ? selectedId : null;
  const siteLayer = otherId ? siteLayers.find(layer => layer.id === otherId) ?? null : null;
  const barrier = otherId && !siteLayer
    ? acousticBarriers.find(item => item.id === otherId) ?? null
    : null;
  const exclusion = otherId && !siteLayer && !barrier
    ? (activeLayout.exclusions?.zones ?? []).find(zone => zone.id === otherId) ?? null
    : null;
  const parcel = otherId && !siteLayer && !barrier && !exclusion
    ? parcels.find(item => item.id === otherId) ?? null
    : null;
  // Draft numeric fields commit on blur/Enter so mid-typing never resolves.
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Component rename draft — same commit-on-blur contract, keyed to the
  // selection so switching components never leaks an unsaved name.
  const [componentNameDraft, setComponentNameDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft({});
    setComponentNameDraft(null);
  }, [selectedId]);
  const commitComponentName = (): void => {
    if (componentNameDraft === null || rectangle == null) return;
    if (componentNameDraft.trim() !== rectangle.label) {
      onPatch(renameComponent(activeLayout, rectangle.id, componentNameDraft));
    }
    setComponentNameDraft(null);
  };

  if (!rectangle || !component) {
    if (siteLayer) {
      return (
        <SiteLayerCard
          layer={siteLayer}
          readOnly={siteLayerReadOnly ?? readOnly}
          locked={selectedLayerLocked}
          {...(onPatchSiteLayer ? { onPatchSiteLayer } : {})}
          {...(onDeleteSiteLayer ? { onDeleteSiteLayer } : {})}
        />
      );
    }
    if (barrier) return <BarrierCard barrier={barrier} />;
    if (exclusion) return <ExclusionCard zone={exclusion} />;
    if (parcel) return <ParcelCard parcel={parcel} isTarget={testFit.parcel?.id === parcel.id} />;
    return (
      <p className="p-3 text-[10px] text-text-muted" data-testid="studio-inspector-empty">
        Select a component or a layer on the canvas or in the contents tree to inspect it.
      </p>
    );
  }

  const field = (key: string, committed: string): string => draft[key] ?? committed;
  const clearDraft = (key: string) => {
    setDraft(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  const commit = (
    key: string,
    apply: (value: number) => void,
    min?: string | number,
    max?: string | number,
  ) => {
    const raw = draft[key];
    if (raw === undefined) return;
    const value = Number(raw.trim());
    const minimum = min == null ? null : Number(min);
    const maximum = max == null ? null : Number(max);
    if (
      raw.trim() !== ''
      && Number.isFinite(value)
      && (minimum == null || value >= minimum)
      && (maximum == null || value <= maximum)
    ) {
      apply(value);
    }
    // Blank, non-finite and out-of-range drafts revert to the last solver
    // value instead of silently becoming zero (or a clamped 0.01 m shape).
    clearDraft(key);
  };
  const numericInput = (
    key: string,
    committed: string,
    apply: (value: number) => void,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
  ): React.JSX.Element => (
    <input
      type="number"
      value={field(key, committed)}
      onChange={(event) => setDraft(current => ({ ...current, [key]: event.target.value }))}
      onBlur={() => commit(key, apply, props.min, props.max)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') clearDraft(key);
      }}
      disabled={readOnly}
      className="mt-1 w-full border border-border bg-background p-1.5 text-xs text-text-primary disabled:opacity-50 rounded-md"
      {...props}
    />
  );

  const centroid = component.coordinates.length > 0
    ? {
        lat: component.coordinates.reduce((sum, point) => sum + point.lat, 0) / component.coordinates.length,
        lng: component.coordinates.reduce((sum, point) => sum + point.lng, 0) / component.coordinates.length,
      }
    : null;
  const blockEnvelope = rectangle.kind === 'generation-block'
    ? installedReservations?.generationBlockEnvelope
    : undefined;

  return (
    <div className="min-w-0 space-y-3 p-3" data-testid="studio-inspector">
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">{rectangle.kind}</p>
        {readOnly
          ? <h3 className="mt-0.5 truncate text-xs font-bold text-text-primary" title={rectangle.label}>{rectangle.label}</h3>
          : (
              <input
                value={componentNameDraft ?? rectangle.label}
                onChange={(event) => setComponentNameDraft(event.target.value)}
                onBlur={commitComponentName}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') setComponentNameDraft(null);
                }}
                aria-label="Component name"
                title="Rename this component — the new name follows it across the canvas, layer tree, exports and reports"
                data-testid="studio-component-name"
                className="mt-0.5 w-full rounded-md border border-border bg-background p-1.5 text-xs font-bold text-text-primary"
              />
            )}
        <span className="mt-1 inline-block border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-text-secondary rounded-sm">
          {BASIS_LABELS[rectangle.dimensionBasis] ?? rectangle.dimensionBasis}
        </span>
        {blockEnvelope && (
          <div className="mt-1 flex flex-wrap gap-1" data-testid="studio-inspector-oem-flags">
            <span className={`border px-1.5 py-0.5 text-[9px] uppercase ${blockEnvelope.maintenanceClearanceIncluded ? 'border-success/50 text-success' : 'border-warning/50 text-warning'} rounded-sm`}>
              Maintenance clearance {blockEnvelope.maintenanceClearanceIncluded ? 'included' : 'NOT included'}
            </span>
            <span className={`border px-1.5 py-0.5 text-[9px] uppercase ${blockEnvelope.fireAccessIncluded ? 'border-success/50 text-success' : 'border-warning/50 text-warning'} rounded-sm`}>
              Fire access {blockEnvelope.fireAccessIncluded ? 'included' : 'NOT included'}
            </span>
          </div>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border text-[10px]">
        <div className="bg-background p-2"><dt className="text-text-muted">Footprint</dt><dd className="mt-0.5 font-bold">{rectangle.widthM.toFixed(1)} × {rectangle.heightM.toFixed(1)} m</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Area</dt><dd className="mt-0.5 font-bold">{rectangle.areaAcres.toFixed(2)} ac</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Layout frame</dt><dd className="mt-0.5 font-bold tabular-nums">{rectangle.xM.toFixed(1)}, {rectangle.yM.toFixed(1)} m</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">World centroid</dt><dd className="mt-0.5 font-bold tabular-nums">{centroid ? `${centroid.lat.toFixed(5)}, ${centroid.lng.toFixed(5)}` : '—'}</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Contained</dt><dd className="mt-0.5 font-bold">{component.containedAcres == null ? 'Unverified' : `${component.containedAcres.toFixed(2)} ac`}</dd></div>
        <div className="bg-background p-2"><dt className="text-text-muted">Edge clearance</dt><dd className={`mt-0.5 font-bold ${component.meetsParcelEdgeSetback === false ? 'text-danger' : ''}`}>{component.parcelEdgeClearanceM == null ? '—' : `${(component.parcelEdgeClearanceM / METRES_PER_FOOT).toFixed(0)} ft`}</dd></div>
        {(component.exclusionOverlapAcres ?? 0) > 0.01 && (
          <div className="bg-background p-2"><dt className="text-text-muted">On exclusions</dt><dd className="mt-0.5 font-bold text-danger">{component.exclusionOverlapAcres!.toFixed(2)} ac</dd></div>
        )}
        {(component.obstacleOverlapAcres ?? 0) > 0.01 && (
          <div className="bg-background p-2"><dt className="text-text-muted">Component overlap</dt><dd className="mt-0.5 font-bold text-danger">{component.obstacleOverlapAcres!.toFixed(2)} ac</dd></div>
        )}
      </dl>

      {/* Pinned equipment: exact numeric placement in the layout frame. */}
      {pinned && (
        <div data-testid="studio-inspector-pinned-editor">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Pinned placement (layout metres)</p>
          <div className="mt-1 grid grid-cols-3 gap-2">
            <label className="text-[9px] uppercase text-text-muted">X
              {numericInput('xM', rectangle.xM.toFixed(1), (value) => onPatch(upsertManualPlacement(activeLayout, {
                componentId: rectangle.id,
                xM: value,
                yM: rectangle.yM,
                rotationDeg: rectangle.rotationDeg ?? 0,
              })), { step: 1, 'data-testid': 'studio-pin-x' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
            <label className="text-[9px] uppercase text-text-muted">Y
              {numericInput('yM', rectangle.yM.toFixed(1), (value) => onPatch(upsertManualPlacement(activeLayout, {
                componentId: rectangle.id,
                xM: rectangle.xM,
                yM: value,
                rotationDeg: rectangle.rotationDeg ?? 0,
              })), { step: 1, 'data-testid': 'studio-pin-y' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
            <label className="text-[9px] uppercase text-text-muted">Rot °
              {numericInput('rot', (rectangle.rotationDeg ?? 0).toFixed(0), (value) => onPatch(upsertManualPlacement(activeLayout, {
                componentId: rectangle.id,
                xM: rectangle.xM,
                yM: rectangle.yM,
                rotationDeg: value,
              })), { step: 5, min: -360, max: 360, 'data-testid': 'studio-pin-rotation' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
          </div>
        </div>
      )}

      {/* Rect customs: editable footprint + world bearing. */}
      {custom && rectShape && (
        <div data-testid="studio-inspector-custom-editor">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Custom footprint (metres, world bearing)</p>
          <div className="mt-1 grid grid-cols-3 gap-2">
            <label className="text-[9px] uppercase text-text-muted">W
              {numericInput('customW', rectShape.widthM.toFixed(1), (value) => onPatch(patchCustomComponent(activeLayout, custom.id, {
                shape: { ...rectShape, widthM: Math.max(0.01, value) },
              })), { step: 1, min: 0.01, 'data-testid': 'studio-custom-width' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
            <label className="text-[9px] uppercase text-text-muted">H
              {numericInput('customH', rectShape.heightM.toFixed(1), (value) => onPatch(patchCustomComponent(activeLayout, custom.id, {
                shape: { ...rectShape, heightM: Math.max(0.01, value) },
              })), { step: 1, min: 0.01, 'data-testid': 'studio-custom-height' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
            <label className="text-[9px] uppercase text-text-muted">Rot °
              {numericInput('customRot', (custom.rotationDeg ?? 0).toFixed(0), (value) => onPatch(patchCustomComponent(activeLayout, custom.id, {
                rotationDeg: value,
              })), { step: 5, min: -360, max: 360, 'data-testid': 'studio-custom-rotation' } as React.InputHTMLAttributes<HTMLInputElement>)}
            </label>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          {!custom && !pinned && rectangle.kind !== 'access-road' && (
            <button
              type="button"
              onClick={() => onPatch(upsertManualPlacement(activeLayout, {
                componentId: rectangle.id,
                xM: rectangle.xM,
                yM: rectangle.yM,
                rotationDeg: 0,
                pinnedAt: new Date().toISOString(),
              }))}
              className="flex items-center gap-1 border border-primary px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary rounded-md"
              data-testid="studio-pin-here"
              title="Fix this component at its current position; the packer flows the rest around it"
            >
              <Pin className="h-3 w-3" aria-hidden /> Pin here
            </button>
          )}
          {pinned && (
            <button
              type="button"
              onClick={() => onPatch(removeManualPlacement(activeLayout, rectangle.id))}
              className="flex items-center gap-1 border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary rounded-md"
              data-testid="studio-unpin"
              title="Return this component to automatic packing"
            >
              <PinOff className="h-3 w-3" aria-hidden /> Unpin
            </button>
          )}
          {custom && (
            <button
              type="button"
              onClick={() => {
                onPatch(removeCustomComponent(activeLayout, custom.id));
                onSelect(null);
              }}
              className="flex items-center gap-1 border border-danger px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-danger rounded-md"
              data-testid="studio-delete-custom"
            >
              <Trash2 className="h-3 w-3" aria-hidden /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};
